import { useRef, useCallback, useState } from 'react';
import {
    Bold,
    Italic,
    Strikethrough,
    Heading1,
    Heading2,
    Heading3,
    List,
    ListOrdered,
    Link,
    Quote,
    Minus,
    Eye,
    Pencil,
} from 'lucide-react';
import { MarkdownRenderer } from './MarkdownRenderer';

interface MarkdownEditorProps {
    value: string;
    onChange: (value: string) => void;
    placeholder?: string;
    rows?: number;
}

interface ToolbarAction {
    icon: typeof Bold;
    label: string;
    prefix: string;
    suffix: string;
    block?: boolean;
}

const actions: ToolbarAction[] = [
    { icon: Bold, label: 'Bold', prefix: '**', suffix: '**' },
    { icon: Italic, label: 'Italic', prefix: '_', suffix: '_' },
    { icon: Strikethrough, label: 'Strikethrough', prefix: '~~', suffix: '~~' },
    { icon: Heading1, label: 'H1', prefix: '# ', suffix: '', block: true },
    { icon: Heading2, label: 'H2', prefix: '## ', suffix: '', block: true },
    { icon: Heading3, label: 'H3', prefix: '### ', suffix: '', block: true },
    { icon: Quote, label: 'Quote', prefix: '> ', suffix: '', block: true },
    { icon: List, label: 'Bullet list', prefix: '- ', suffix: '', block: true },
    { icon: ListOrdered, label: 'Numbered list', prefix: '1. ', suffix: '', block: true },
    { icon: Link, label: 'Link', prefix: '[', suffix: '](url)' },
    { icon: Minus, label: 'Divider', prefix: '\n---\n', suffix: '', block: true },
];

export function MarkdownEditor({ value, onChange, placeholder, rows = 8 }: MarkdownEditorProps) {
    const textareaRef = useRef<HTMLTextAreaElement>(null);
    const [preview, setPreview] = useState(false);

    const applyAction = useCallback(
        (action: ToolbarAction) => {
            const ta = textareaRef.current;
            if (!ta) return;

            const start = ta.selectionStart;
            const end = ta.selectionEnd;
            const selected = value.slice(start, end);

            let newText: string;
            let cursorPos: number;

            if (action.block && !selected) {
                const lineStart = value.lastIndexOf('\n', start - 1) + 1;
                newText = value.slice(0, lineStart) + action.prefix + value.slice(lineStart);
                cursorPos = lineStart + action.prefix.length;
            } else {
                newText =
                    value.slice(0, start) +
                    action.prefix +
                    (selected || action.label) +
                    action.suffix +
                    value.slice(end);
                cursorPos = start + action.prefix.length + (selected || action.label).length;
            }

            onChange(newText);

            requestAnimationFrame(() => {
                ta.focus();
                ta.setSelectionRange(cursorPos, cursorPos);
            });
        },
        [value, onChange],
    );

    return (
        <div className="rounded-md border border-slate-700 overflow-hidden">
            {/* Toolbar */}
            <div className="flex flex-wrap items-center gap-0.5 border-b border-slate-700 bg-slate-800/50 px-2 py-1.5">
                {actions.map((action) => (
                    <button
                        key={action.label}
                        type="button"
                        title={action.label}
                        onClick={() => applyAction(action)}
                        className="rounded p-1.5 text-slate-400 hover:bg-slate-700 hover:text-slate-200 transition-colors"
                    >
                        <action.icon className="h-3.5 w-3.5" />
                    </button>
                ))}
                <div className="flex-1" />
                <button
                    type="button"
                    onClick={() => setPreview(!preview)}
                    className={`flex items-center gap-1 rounded px-2 py-1 text-xs transition-colors ${
                        preview
                            ? 'bg-violet-900/40 text-violet-300'
                            : 'text-slate-400 hover:bg-slate-700 hover:text-slate-200'
                    }`}
                >
                    {preview ? <Pencil className="h-3 w-3" /> : <Eye className="h-3 w-3" />}
                    {preview ? 'Edit' : 'Preview'}
                </button>
            </div>

            {/* Content */}
            {preview ? (
                <div className="min-h-[120px] px-3 py-2 bg-slate-800">
                    {value ? (
                        <MarkdownRenderer content={value} />
                    ) : (
                        <p className="text-sm italic text-slate-500">Nothing to preview</p>
                    )}
                </div>
            ) : (
                <textarea
                    ref={textareaRef}
                    value={value}
                    onChange={(e) => onChange(e.target.value)}
                    placeholder={placeholder}
                    rows={rows}
                    className="w-full bg-slate-800 px-3 py-2 text-sm text-slate-100 font-mono placeholder:text-slate-500 focus-visible:outline-none resize-y min-h-[120px]"
                />
            )}
        </div>
    );
}
